import React, { useRef, useState, useCallback, useEffect } from "react";

interface CodeInputProps {
  onComplete: (code: string) => void;
  disabled?: boolean;
  error?: string;
}

const CODE_LENGTH = 6;

export default function CodeInput({
  onComplete,
  disabled = false,
  error,
}: CodeInputProps) {
  const [values, setValues] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const focusInput = (index: number) => {
    const clamped = Math.max(0, Math.min(CODE_LENGTH - 1, index));
    inputRefs.current[clamped]?.focus();
  };

  const triggerComplete = useCallback(
    (nextValues: string[]) => {
      const code = nextValues.join("");
      if (code.length === CODE_LENGTH) {
        onComplete(code);
      }
    },
    [onComplete]
  );

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);

    setValues((prev) => {
      const next = [...prev];
      next[index] = digit;

      if (digit && index < CODE_LENGTH - 1) {
        focusInput(index + 1);
      }

      triggerComplete(next);
      return next;
    });
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace") {
      if (values[index]) {
        setValues((prev) => {
          const next = [...prev];
          next[index] = "";
          return next;
        });
      } else if (index > 0) {
        focusInput(index - 1);
        setValues((prev) => {
          const next = [...prev];
          next[index - 1] = "";
          return next;
        });
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      focusInput(index - 1);
    } else if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);

    if (!pasted) return;

    const next = Array(CODE_LENGTH).fill("");
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }

    setValues(next);

    const lastFilledIndex = Math.min(pasted.length - 1, CODE_LENGTH - 1);
    focusInput(lastFilledIndex);
    triggerComplete(next);
  };

  return (
    <div>
      <div className="d-flex gap-2 justify-content-center">
        {Array.from({ length: CODE_LENGTH }, (_, i) => (
          <input
            key={i}
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={values[i]}
            disabled={disabled}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            className={`code-input__box form-control text-center fw-semibold fs-4 p-0${
              error ? " is-invalid" : ""
            }`}
            aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`}
          />
        ))}
      </div>
      {error && (
        <div className="text-danger small mt-2 text-center" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
